// src/components/auth/SignupForm.jsx
// =============================================
// SignupForm.jsx
// Handles user registration, prioritizing Google Sign-Up.
// Includes a clear, upfront terms acceptance requirement.
// =============================================

import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, AlertCircle } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import { TermsAndPrivacy } from './TermsAndPrivacy';
import { HousePortal } from '@/App';

export function SignupForm({ onModeChange }) {
    const [formData, setFormData] = useState({
        firstName: '',
        email: '',
        password: ''
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [authState, setAuthState] = useState(() => authManager.getState());

    useEffect(() => {
        const unsubscribe = authManager.subscribe(state => {
            setAuthState(state);
            setLoading(state.isAuthenticating);
            
            if (!state.isAuthenticating) {
                setEmailLoading(false);
                setGoogleLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const createDefaultProfile = (displayName, email) => ({
        displayName,
        email,
        termsAccepted: new Date().toISOString(),
        profileData: {
            lastUpdated: Date.now(),
            theme: 'dark',
            headerVariant: 'FULL',
            chatFontSize: 'medium',
            codeFontSize: 'medium',
            codeLayout: 'default',
            notifications: { sound: true, desktop: true },
            instructions: { global: { enabled: false, content: '' } },
            botSwitchBehavior: { openInNewTab: true },
            pinnedBots: [],
            userMeta: {
                techStack: [],
                environment: { os: '', ide: '' },
                additionalDetails: ''
            },
            setup: {
                completed: false,
                step: 'welcome'
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!termsAccepted) {
            setError('Please accept the Terms of Service and Privacy Policy to continue');
            return;
        }

        setError(null);
        setEmailLoading(true);

        try {
            const user = await authManager.signUpWithEmail(formData.email, formData.password);
            const profileData = createDefaultProfile(formData.firstName, formData.email);
            await authManager.createUserProfile(profileData);
            window.location.href = '/';
        } catch (err) {
            setError(formatAuthError(err.message));
            setEmailLoading(false);
        }
    };

    const handleGoogleSignUp = async () => {
        if (!termsAccepted) {
            setError('Please accept the Terms of Service and Privacy Policy to continue');
            return;
        }

        setError(null);
        setGoogleLoading(true);

        try {
            const userCredential = await authManager.signInWithGoogle();
            
            if (userCredential.additionalUserInfo && !userCredential.additionalUserInfo.isNewUser) {
                setError('An account with this Google account already exists. Please sign in instead.');
                setGoogleLoading(false);
                return;
            }

            const profileData = createDefaultProfile(
                userCredential.user.displayName || formData.firstName,
                userCredential.user.email
            );
            await authManager.createUserProfile(profileData);
            window.location.href = '/';
        } catch (err) {
            setError(formatAuthError(err.message));
            setGoogleLoading(false);
        }
    };

    const formatAuthError = (message) => {
        const errorMap = {
            'auth/email-already-in-use': 'An account already exists with this email',
            'auth/invalid-email': 'Please enter a valid email address',
            'auth/weak-password': 'Password should be at least 6 characters',
            'auth/network-request-failed': 'Network error. Please check your connection.',
            'auth/popup-closed-by-user': 'Sign up cancelled. Please try again.',
            'Failed to create user profile': 'Failed to set up your account. Please try again.'
        };
        
        const errorCode = message.match(/auth\/[\w-]+/)?.[0];
        return errorMap[errorCode] || errorMap[message] || 'An error occurred. Please try again.';
    };

    return (
        <>
            <div className={`space-y-4 transition-opacity duration-200
                          ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
                {error && (
                    <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <p>{error}</p>
                    </div>
                )}

                {/* Step 1: Name */}
                <div>
                    <label className="block text-sm font-medium text-on-surface mb-1.5">
                        What should your bots call you?
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim w-4 h-4" />
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                            className={`w-full pl-9 pr-4 py-2.5 bg-surface/60 backdrop-blur-sm rounded-lg
                                    text-sm text-on-surface placeholder:text-on-surface-dim/50
                                    border border-white/10 focus:border-primary/50
                                    focus:outline-none focus:ring-2 focus:ring-primary/20
                                    transition-all duration-200
                                    ${loading ? 'cursor-not-allowed' : ''}`}
                            placeholder="First name"
                            required
                            autoComplete="given-name"
                            autoFocus
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Step 2: Terms Agreement */}
                <div className="flex items-start space-x-3 pt-2">
                    <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="h-4 w-4 mt-1 flex-shrink-0 rounded bg-surface/60 backdrop-blur-sm border-white/10 text-primary focus:ring-primary/50"
                    />
                    <div className="text-sm">
                        <label htmlFor="terms" className="text-on-surface-dim">
                            I agree to the{' '}
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowTerms(true)}
                            className="font-medium text-primary-light hover:text-primary transition-colors underline"
                        >
                            Terms & Privacy Policy
                        </button>
                    </div>
                </div>

                {/* Step 3: Google Sign-Up (Primary) */}
                <div className="pt-2">
                     <button
                        type="button"
                        onClick={handleGoogleSignUp}
                        disabled={loading}
                        className="w-full py-2.5 px-4 bg-surface/60 backdrop-blur-sm border border-white/10 rounded-lg
                                hover:bg-surface/80 
                                text-sm text-on-surface font-medium
                                transition-all duration-200
                                focus:outline-none focus:ring-2 focus:ring-primary/20
                                disabled:opacity-50 disabled:cursor-not-allowed
                                flex items-center justify-center space-x-2 relative"
                    >
                        <span className={`flex items-center justify-center gap-2
                                    transition-opacity duration-200
                                    ${googleLoading ? 'opacity-0' : 'opacity-100'}`}>
                            <svg className="w-4 h-4" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            <span>Sign Up with Google</span>
                        </span>
                        {googleLoading && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                            </span>
                        )}
                    </button>
                </div>

                {/* Divider */}
                <div className="relative pt-2">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border-color"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-background text-on-surface-dim">Or with Email & Password</span>
                    </div>
                </div>

                {/* Step 4: Email & Password Sign-Up (Secondary) */}
                <form onSubmit={handleSubmit} className="space-y-4">
                     <div>
                        <label className="block text-sm font-medium text-on-surface mb-1.5 sr-only">
                            Email
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim w-4 h-4" />
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                className={`w-full pl-9 pr-4 py-2.5 bg-surface/60 backdrop-blur-sm rounded-lg
                                        text-sm text-on-surface placeholder:text-on-surface-dim/50
                                        border border-white/10 focus:border-primary/50
                                        focus:outline-none focus:ring-2 focus:ring-primary/20
                                        transition-all duration-200
                                        ${loading ? 'cursor-not-allowed' : ''}`}
                                placeholder="name@youremail.com"
                                required
                                autoComplete="email"
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-on-surface mb-1.5 sr-only">
                            Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim w-4 h-4" />
                            <input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                className={`w-full pl-9 pr-4 py-2.5 bg-surface/60 backdrop-blur-sm rounded-lg
                                        text-sm text-on-surface placeholder:text-on-surface-dim/50
                                        border border-white/10 focus:border-primary/50
                                        focus:outline-none focus:ring-2 focus:ring-primary/20
                                        transition-all duration-200
                                        ${loading ? 'cursor-not-allowed' : ''}`}
                                placeholder="••••••••"
                                required
                                autoComplete="new-password"
                                disabled={loading}
                            />
                        </div>
                        <p className="mt-1 text-xs text-on-surface-dim">
                            At least 6 characters required
                        </p>
                    </div>

                     <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2.5 px-4 bg-gradient-primary
                                hover:opacity-90
                                rounded-lg text-sm text-white font-medium
                                transform transition-all duration-200
                                hover:shadow-button active:scale-[0.98]
                                focus:outline-none focus:ring-2 focus:ring-primary/30
                                disabled:opacity-50 disabled:cursor-not-allowed
                                relative"
                    >
                        <span className={`flex items-center justify-center gap-2
                                    transition-opacity duration-200
                                    ${emailLoading ? 'opacity-0' : 'opacity-100'}`}>
                            Create Account with Email
                        </span>
                        {emailLoading && (
                            <span className="absolute inset-0 flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                <span className="ml-2 text-sm">Creating account...</span>
                            </span>
                        )}
                    </button>
                </form>

                {/* Footer Link */}
                <p className="text-center text-sm text-on-surface-dim pt-2">
                    Already have an account?{' '}
                    <button
                        type="button"
                        onClick={onModeChange}
                        className="text-primary-light hover:text-primary transition-colors font-medium"
                        disabled={loading}
                    >
                        Sign in
                    </button>
                </p>
            </div>

            {showTerms && (
                <HousePortal>
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[2000] p-4">
                        <TermsAndPrivacy 
                            onClose={() => setShowTerms(false)}
                            initialTab={'terms'}
                        />
                    </div>
                </HousePortal>
            )}
        </>
    );
}

export default SignupForm;